import Phaser from 'phaser';
import type { GameStats, PlayerStats } from '../../api/stats-types.js';
import type { GameState, Player, PlayerId } from '../../game/types.js';
import { mapsById } from '../../maps/index.js';
import {
  fetchGameSnapshot,
  fetchGameStats,
  type RemoteGameSnapshot,
} from '../../store/RemoteGameStore.js';
import { getAuth } from '../../store/auth.js';
import { navigateBack } from '../sceneNav.js';
import { attachBottomNav, preloadNavAssets } from '../view/AppNav.js';
import { Button } from '../view/Button.js';
import { drawDiceFairnessChart } from '../view/DiceFairnessChart.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

/**
 * Post-game results page. Reached three ways:
 *
 *   1. Email CTA — "View results" in the game-ended email links to
 *      /game/:id; JoinScene detects status=finished and starts here.
 *   2. Inbox — tapping a finished game card does the same.
 *   3. Direct URL — sharing the game link after the game is done.
 *
 * Layout mirrors the rest of the app:
 *   - Top header band with Back button (matches Inbox / Settings / About)
 *   - Wide BattleShark logo (matches JoinScene + Inbox)
 *   - Cyan accent line
 *   - Big winner card with crown glyph + amber border (the headline)
 *   - Map + game code meta row
 *   - "PODIUM" card with ranked rows
 *   - "STATISTICS" placeholder card — reserved slot for v1.0 detailed stats
 */
const PAGE_BG_INT = 0x040713;
const HEADER_BAND_HEIGHT = 88;
const SIDE_PADDING = 36;

const WINNER_CARD_WIDTH = 560;
// Tall enough to also house the meta row (MAP · GAMECODE) inside
// the amber border — the previous layout split the meta line off
// below the card, leaving "N/TOTAL TILES" cramped against the
// bottom edge.
const WINNER_CARD_HEIGHT = 252;
const WINNER_CARD_RADIUS = 10;

const CARD_RADIUS = 8;
const CARD_WIDTH = DESIGN_WIDTH - SIDE_PADDING * 2;

const PODIUM_ROW_HEIGHT = 72;
const PODIUM_LABEL_AREA = 44;
const PODIUM_BOTTOM_PAD = 18;

// Stats card sections — sized to keep the page non-scrolling for
// up to 4 players. Header + aggregates rows are fixed; per-player
// rows are 64px each (chip+name on the left, dice histogram on the
// right). Total height = STATS_HEADER + STATS_AGG_HEIGHT +
// playerCount * STATS_ROW_HEIGHT + STATS_BOTTOM_PAD.
const STATS_HEADER_HEIGHT = 44;
const STATS_AGG_HEIGHT = 78;
const STATS_ROW_HEIGHT = 64;
const STATS_BOTTOM_PAD = 18;
/** Height used when stats are still loading / unavailable — keeps
 *  the layout stable while the fetch resolves. */
const STATS_PLACEHOLDER_HEIGHT = 130;

interface InitData {
  readonly gameId?: string;
  /** When routed from JoinScene the snapshot is already in hand;
   *  pass it through to skip the redundant fetch. Standalone navs
   *  (direct URL, future deep-links) omit this and trigger a fetch
   *  in create(). */
  readonly snapshot?: RemoteGameSnapshot;
}

interface PodiumRow {
  readonly rank: number;
  readonly player: Player;
  readonly isLocal: boolean;
  readonly tileCount: number;
}

export class ResultsScene extends Phaser.Scene {
  static readonly KEY = 'Results';

  private gameId = '';
  private snapshot: RemoteGameSnapshot | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;

  /** Stats fetch lives independently from the snapshot fetch so the
   *  page renders the podium quickly and the stats card swaps from
   *  "loading" to real data when ready (or fails soft to the legacy
   *  placeholder). */
  private stats: GameStats | null = null;
  private statsLoadState: 'idle' | 'loading' | 'loaded' | 'unavailable' | 'error' =
    'idle';
  private statsUnavailableReason: 'lobby' | 'no_initial_seed' | 'unknown_map' | null =
    null;
  /** Container the stats card renders into. Cleared + redrawn when
   *  the stats fetch resolves so the placeholder swaps cleanly. */
  private statsContainer: Phaser.GameObjects.Container | null = null;
  private statsTopY = 0;

  constructor() {
    super(ResultsScene.KEY);
  }

  preload(): void {
    preloadNavAssets(this);
    // Reuse the wide logo asset shared by JoinScene + InboxScene.
    // Leading slash so Phaser resolves against site root regardless
    // of current URL path.
    this.load.image('resultsWideLogo', '/assets/logo2.png');
  }

  init(data?: InitData): void {
    this.gameId = data?.gameId ?? '';
    this.snapshot = data?.snapshot ?? null;
  }

  create(): void {
    this.drawBackground();
    this.drawHeaderBand();
    this.drawWideLogo();
    this.drawAccentLine();
    attachBottomNav(this, 'games', { allowActiveTap: true });

    if (!this.gameId) {
      this.showStatus('No game id.');
      return;
    }

    if (this.snapshot) {
      this.render();
      // Stats fetch still needed — JoinScene handed us the snapshot
      // but stats are computed by a separate endpoint.
      void this.bootstrapStats();
      return;
    }

    this.showStatus('Loading…');
    void this.bootstrap();
  }

  /** Fetch + render the stats card. Independent of the snapshot
   *  fetch — kicks off as soon as we know the gameId. Updates the
   *  stats card in-place when it resolves. */
  private async bootstrapStats(): Promise<void> {
    if (!this.gameId) return;
    this.statsLoadState = 'loading';
    try {
      const result = await fetchGameStats(this.gameId);
      if (!this.scene.isActive()) return;
      if (result.kind === 'ok') {
        this.stats = result.stats;
        this.statsLoadState = 'loaded';
      } else {
        this.statsUnavailableReason = result.reason;
        this.statsLoadState = 'unavailable';
      }
    } catch (err) {
      console.error('ResultsScene stats load failed', err);
      if (!this.scene.isActive()) return;
      this.statsLoadState = 'error';
    }
    // Re-render the stats card with whatever state we landed in.
    // The podium + winner card don't depend on stats, so we leave
    // them alone — just swap the stats card contents.
    this.redrawStatsCard();
  }

  private async bootstrap(): Promise<void> {
    // Fire stats fetch in parallel with the snapshot fetch — stats
    // render into their own card and don't gate the rest of the page.
    void this.bootstrapStats();
    try {
      this.snapshot = await fetchGameSnapshot(this.gameId);
      if (!this.scene.isActive()) return;
      this.render();
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      if (status === 404) {
        this.showStatus("This game doesn't exist (or you weren't in it).");
        return;
      }
      console.error('ResultsScene load failed', err);
      this.showStatus('Could not load results.');
    }
  }

  // --- Background + chrome ---

  private drawBackground(): void {
    const g = this.add.graphics();
    g.fillStyle(PAGE_BG_INT, 1);
    g.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
  }

  private drawHeaderBand(): void {
    const band = this.add.graphics();
    band.fillStyle(PAGE_BG_INT, 1);
    band.fillRect(0, 0, DESIGN_WIDTH, HEADER_BAND_HEIGHT);
    band.lineStyle(1, COLORS.borderSubtleInt, 0.8);
    band.beginPath();
    band.moveTo(0, HEADER_BAND_HEIGHT);
    band.lineTo(DESIGN_WIDTH, HEADER_BAND_HEIGHT);
    band.strokePath();
    band.setDepth(100);

    const back = new Button(this, {
      x: 90,
      y: HEADER_BAND_HEIGHT / 2 + 8,
      width: 120,
      height: 52,
      text: '< Back',
      onTap: () => navigateBack(this),
    });
    back.setDepth(101);
  }

  /**
   * Tracks the rendered bottom edge of the wide logo so subsequent
   * sections can flow from there. Set in drawWideLogo, read by
   * drawAccentLine and render().
   */
  private logoBottomY = 0;

  private drawWideLogo(): void {
    const logo = this.add
      .image(DESIGN_WIDTH / 2, HEADER_BAND_HEIGHT, 'resultsWideLogo')
      .setOrigin(0.5, 0);
    // Force full design width — no min() cap. The source asset has
    // built-in side padding; capping at logo.width left visible
    // black margins on each edge. Scaling to DESIGN_WIDTH pushes
    // the visible brand mark all the way to the page edges where
    // the header band's accent line already lives.
    const scale = DESIGN_WIDTH / logo.width;
    logo.setScale(scale);
    this.logoBottomY = HEADER_BAND_HEIGHT + logo.displayHeight;
  }

  private drawAccentLine(): void {
    const accent = this.add.graphics();
    accent.lineStyle(1, COLORS.accentCyanInt, 0.85);
    const margin = 80;
    // Sit 12px below the rendered logo bottom — keeps the visual
    // rhythm stable regardless of the logo asset's aspect ratio.
    const y = this.logoBottomY + 12;
    accent.lineBetween(margin, y, DESIGN_WIDTH - margin, y);
  }

  private showStatus(text: string): void {
    this.statusText?.destroy();
    this.statusText = this.add
      .text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, text, {
        fontFamily: FONT_HEADER,
        fontSize: '22px',
        color: COLORS.textPrimary,
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);
  }

  // --- Render the actual results ---

  private render(): void {
    this.statusText?.destroy();
    this.statusText = null;

    const snap = this.snapshot;
    if (!snap) return;
    const state = snap.state as GameState | null;
    if (!state || state.status.kind !== 'victory') {
      this.showStatus('This game has no recorded result.');
      return;
    }

    const myId = getAuth().user?.id ?? '';
    const podium = buildPodium(state, myId);
    const survivors = state.players.filter((p) => !p.eliminated);
    const teamWin = survivors.length > 1;
    const localWon = survivors.some((p) => p.id === myId);
    // Narrowed here so the helper doesn't need to re-prove that
    // status.kind === 'victory' — TS forgets the narrowing once we
    // hand `state` to a helper. Pull winner id eagerly instead.
    const winnerId = state.status.winner;

    // Total tile count for the dominance bar — drives the winner's
    // "N / TOTAL tiles" line. Counts only owned tiles, which equals
    // the map total in any normal game.
    const totalTiles = Object.values(state.territories).filter(
      (t) => t.owner != null
    ).length;

    // Winner card occupies the headline region. Tracking y manually
    // so each section can flow below the previous one. Starts a bit
    // below the accent line (logoBottomY + 12) for breathing room.
    let y = this.logoBottomY + 32;
    y = this.drawWinnerCard(
      y,
      state,
      winnerId,
      localWon,
      teamWin,
      podium,
      totalTiles,
      snap.mapId
    );
    y += 28;
    y = this.drawPodiumCard(y, podium);
    y += 22;
    y = this.drawStatsCard(y);
  }

  /**
   * Big winner card with amber border, crown glyph, headline, and a
   * tile-dominance bar. The visual headline of the page — replaces
   * the previous bare "VICTORY / DEFEAT" text treatment.
   */
  private drawWinnerCard(
    top: number,
    state: GameState,
    winnerId: PlayerId,
    localWon: boolean,
    teamWin: boolean,
    podium: PodiumRow[],
    totalTiles: number,
    mapId: string
  ): number {
    const cx = DESIGN_WIDTH / 2;
    const left = cx - WINNER_CARD_WIDTH / 2;

    const bg = this.add.graphics();
    bg.fillStyle(0x14110a, 1); // warmer tint than the page bg
    bg.fillRoundedRect(
      left,
      top,
      WINNER_CARD_WIDTH,
      WINNER_CARD_HEIGHT,
      WINNER_CARD_RADIUS
    );
    bg.lineStyle(
      2,
      localWon ? COLORS.accentAmberInt : COLORS.accentRedInt,
      1
    );
    bg.strokeRoundedRect(
      left,
      top,
      WINNER_CARD_WIDTH,
      WINNER_CARD_HEIGHT,
      WINNER_CARD_RADIUS
    );

    // Crown glyph for victory; muted skull-equivalent (just empty)
    // for defeat — keeps the layout balanced without leaning hard
    // into a "you lost" punishment.
    const glyph = localWon ? '👑' : '·  ·  ·';
    this.add
      .text(cx, top + 38, glyph, {
        fontFamily: FONT_HEADER,
        fontSize: localWon ? '40px' : '28px',
        color: localWon ? COLORS.accentAmberHex : COLORS.textMuted,
      })
      .setOrigin(0.5);

    // Headline VICTORY / DEFEAT
    const titleColor = localWon
      ? COLORS.accentAmberHex
      : COLORS.accentRedHex;
    this.add
      .text(cx, top + 86, localWon ? 'VICTORY' : 'DEFEAT', {
        fontFamily: FONT_HEADER,
        fontSize: '52px',
        color: titleColor,
        fontStyle: '700',
      })
      .setLetterSpacing(8)
      .setOrigin(0.5);

    // Winner subtitle — "<winner> wins" or "TEAM VICTORY".
    const winnerById = new Map(state.players.map((p) => [p.id, p]));
    const winnerPlayer = winnerById.get(winnerId);
    const subtitle = teamWin
      ? localWon
        ? 'TEAM VICTORY'
        : 'TEAM DEFEAT'
      : localWon
        ? 'YOU WIN'
        : winnerPlayer
          ? `${winnerPlayer.name.toUpperCase()} WINS`
          : 'GAME OVER';
    this.add
      .text(cx, top + 130, subtitle, {
        fontFamily: FONT_HEADER,
        fontSize: '18px',
        color: COLORS.textSecondary,
        fontStyle: '600',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5);

    // Winner's tile count as a horizontal bar — shows dominance at
    // a glance. The winner is rank 1 in podium (or the local player
    // for teams); pull their tile count.
    const winnerRow = podium.find((r) => r.player.id === winnerId);
    const winnerTiles = winnerRow?.tileCount ?? 0;
    const barTop = top + 158;
    const barW = 360;
    const barH = 8;
    const barLeft = cx - barW / 2;
    const fillPct =
      totalTiles > 0 ? Math.min(1, winnerTiles / totalTiles) : 0;
    const barBg = this.add.graphics();
    barBg.fillStyle(COLORS.bgElevatedInt, 1);
    barBg.fillRoundedRect(barLeft, barTop, barW, barH, barH / 2);
    if (fillPct > 0) {
      barBg.fillStyle(titleColor === COLORS.accentAmberHex ? COLORS.accentAmberInt : COLORS.accentRedInt, 1);
      barBg.fillRoundedRect(
        barLeft,
        barTop,
        Math.max(barH, barW * fillPct),
        barH,
        barH / 2
      );
    }
    this.add
      .text(cx, barTop + barH + 14, `${winnerTiles} / ${totalTiles} TILES`, {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(3)
      .setOrigin(0.5, 0);

    // Map · game code meta row, anchored to the bottom of the card.
    // Lives inside the amber border so the card reads as a single
    // contained summary block instead of leaving the meta floating
    // free below it.
    const mapName = mapsById[mapId]?.name ?? mapId;
    this.add
      .text(
        cx,
        top + WINNER_CARD_HEIGHT - 28,
        `${mapName.toUpperCase()}  ·  ${this.gameId.toUpperCase()}`,
        {
          fontFamily: FONT_MONO,
          fontSize: '13px',
          color: COLORS.textMuted,
        }
      )
      .setLetterSpacing(3)
      .setOrigin(0.5);

    return top + WINNER_CARD_HEIGHT;
  }

  /**
   * Podium card — labeled "PODIUM" header with one row per finisher.
   * Rank circle is amber for #1 (matches the winner card's amber
   * accent) and muted gray for the rest.
   */
  private drawPodiumCard(top: number, podium: PodiumRow[]): number {
    const left = SIDE_PADDING;
    const rowCount = podium.length;
    const cardH = PODIUM_LABEL_AREA + rowCount * PODIUM_ROW_HEIGHT + PODIUM_BOTTOM_PAD;

    const bg = this.add.graphics();
    bg.fillStyle(COLORS.bgPanelInt, 1);
    bg.fillRoundedRect(left, top, CARD_WIDTH, cardH, CARD_RADIUS);
    bg.lineStyle(1, COLORS.borderSubtleInt, 1);
    bg.strokeRoundedRect(left, top, CARD_WIDTH, cardH, CARD_RADIUS);

    this.add
      .text(left + 24, top + 22, 'PODIUM', {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);

    // Thin divider below the label
    const divider = this.add.graphics();
    divider.lineStyle(1, COLORS.borderSubtleInt, 0.6);
    divider.lineBetween(
      left + 24,
      top + PODIUM_LABEL_AREA,
      left + CARD_WIDTH - 24,
      top + PODIUM_LABEL_AREA
    );

    const firstRowY = top + PODIUM_LABEL_AREA + PODIUM_ROW_HEIGHT / 2;
    podium.forEach((row, i) => {
      this.drawPodiumRow(left, firstRowY + i * PODIUM_ROW_HEIGHT, row);
    });

    return top + cardH;
  }

  /**
   * One podium row: rank circle, color chip, name + (YOU) tag,
   * mono tile count right-aligned.
   */
  private drawPodiumRow(cardLeft: number, midY: number, row: PodiumRow): void {
    const innerLeft = cardLeft + 24;
    const innerRight = cardLeft + CARD_WIDTH - 24;
    const rankColors: Record<number, string> = {
      1: COLORS.accentAmberHex,
      2: COLORS.textSecondary,
      3: COLORS.textMuted,
      4: COLORS.textMuted,
    };

    // Rank circle — filled amber for #1, outlined for the rest.
    const rankX = innerLeft + 22;
    const rankColor = rankColors[row.rank] ?? COLORS.textMuted;
    const ringInt = Phaser.Display.Color.HexStringToColor(rankColor).color;
    const ring = this.add.graphics();
    if (row.rank === 1) {
      ring.fillStyle(ringInt, 1);
      ring.fillCircle(rankX, midY, 20);
    } else {
      ring.lineStyle(2, ringInt, 1);
      ring.strokeCircle(rankX, midY, 20);
    }
    this.add
      .text(rankX, midY, `${row.rank}`, {
        fontFamily: FONT_HEADER,
        fontSize: '22px',
        color: row.rank === 1 ? '#04070d' : rankColor,
        fontStyle: '700',
      })
      .setOrigin(0.5);

    // Color chip — solid square showing the seat color.
    const chipX = innerLeft + 70;
    const colorInt = Phaser.Display.Color.HexStringToColor(
      row.player.color
    ).color;
    this.add
      .rectangle(chipX, midY, 26, 26, colorInt)
      .setStrokeStyle(1, COLORS.borderBrightInt);

    // Name + optional (YOU) tag.
    const label = row.isLocal
      ? `${row.player.name.toUpperCase()}  (YOU)`
      : row.player.name.toUpperCase();
    this.add
      .text(chipX + 24, midY, label, {
        fontFamily: FONT_HEADER,
        fontSize: '20px',
        color: row.isLocal ? COLORS.accentCyanHex : COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0, 0.5);

    // Tile count right-aligned.
    this.add
      .text(innerRight, midY, `${row.tileCount} tiles`, {
        fontFamily: FONT_MONO,
        fontSize: '14px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(1)
      .setOrigin(1, 0.5);
  }

  /**
   * Stats card — multi-state: loading / loaded / unavailable / error.
   * Owned by a dedicated container (statsContainer) so it can be
   * cleared + redrawn when the stats fetch resolves, without
   * disturbing the rest of the page.
   *
   * Layout (loaded):
   *   ┌──────────────────────────────────────┐
   *   │ STATISTICS                           │
   *   │ ───────────────────────────────────  │
   *   │  TURNS · ATTACKS · TILES · MIN       │  ← top-line aggregates
   *   │ ───────────────────────────────────  │
   *   │ [P1 row: chip+name | dice chart]     │
   *   │ [P2 row: ...]                        │
   *   │ ...                                  │
   *   └──────────────────────────────────────┘
   */
  private drawStatsCard(top: number): number {
    this.statsTopY = top;
    const container = this.add.container(0, 0);
    this.statsContainer = container;
    return top + this.populateStatsCard(container, top);
  }

  /** Tear down + redraw the stats card after the fetch resolves.
   *  Returns nothing — height changes are absorbed by the page
   *  layout below (the stats card is the last section). */
  private redrawStatsCard(): void {
    if (!this.statsContainer) return;
    this.statsContainer.removeAll(true);
    this.populateStatsCard(this.statsContainer, this.statsTopY);
  }

  /** Renders the stats card contents into the given container.
   *  Returns the card height so drawStatsCard can flow content
   *  below. */
  private populateStatsCard(
    container: Phaser.GameObjects.Container,
    top: number
  ): number {
    if (this.statsLoadState === 'loaded' && this.stats) {
      return this.drawStatsCardLoaded(container, top, this.stats);
    }
    return this.drawStatsCardPlaceholder(container, top);
  }

  /** Loading / unavailable / error states. Same dimensions as the
   *  original placeholder card so the page doesn't reflow when the
   *  fetch resolves to an unavailable state. */
  private drawStatsCardPlaceholder(
    container: Phaser.GameObjects.Container,
    top: number
  ): number {
    const left = SIDE_PADDING;

    const bg = this.add.graphics();
    bg.fillStyle(COLORS.bgPanelInt, 1);
    bg.fillRoundedRect(
      left,
      top,
      CARD_WIDTH,
      STATS_PLACEHOLDER_HEIGHT,
      CARD_RADIUS
    );
    bg.lineStyle(1, COLORS.borderSubtleInt, 1);
    bg.strokeRoundedRect(
      left,
      top,
      CARD_WIDTH,
      STATS_PLACEHOLDER_HEIGHT,
      CARD_RADIUS
    );
    container.add(bg);

    const header = this.add
      .text(left + 24, top + 22, 'STATISTICS', {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);
    container.add(header);

    const divider = this.add.graphics();
    divider.lineStyle(1, COLORS.borderSubtleInt, 0.6);
    divider.lineBetween(
      left + 24,
      top + 44,
      left + CARD_WIDTH - 24,
      top + 44
    );
    container.add(divider);

    const copy = this.placeholderCopy();
    const message = this.add
      .text(DESIGN_WIDTH / 2, top + STATS_PLACEHOLDER_HEIGHT / 2 + 8, copy, {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);
    container.add(message);

    return STATS_PLACEHOLDER_HEIGHT;
  }

  private placeholderCopy(): string {
    if (this.statsLoadState === 'loading' || this.statsLoadState === 'idle') {
      return '[ LOADING STATS… ]';
    }
    if (this.statsLoadState === 'unavailable') {
      if (this.statsUnavailableReason === 'lobby') {
        return '[ NO STATS YET — GAME STILL IN LOBBY ]';
      }
      // 'no_initial_seed' or 'unknown_map' — same user-facing copy.
      return '[ STATS UNAVAILABLE FOR THIS GAME ]';
    }
    return '[ COULD NOT LOAD STATS ]';
  }

  private drawStatsCardLoaded(
    container: Phaser.GameObjects.Container,
    top: number,
    stats: GameStats
  ): number {
    const left = SIDE_PADDING;
    const players = Object.entries(stats.perPlayer);
    const cardH =
      STATS_HEADER_HEIGHT +
      STATS_AGG_HEIGHT +
      players.length * STATS_ROW_HEIGHT +
      STATS_BOTTOM_PAD;

    const bg = this.add.graphics();
    bg.fillStyle(COLORS.bgPanelInt, 1);
    bg.fillRoundedRect(left, top, CARD_WIDTH, cardH, CARD_RADIUS);
    bg.lineStyle(1, COLORS.borderSubtleInt, 1);
    bg.strokeRoundedRect(left, top, CARD_WIDTH, cardH, CARD_RADIUS);
    container.add(bg);

    // Header
    const header = this.add
      .text(left + 24, top + 22, 'STATISTICS', {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);
    container.add(header);

    const divider1 = this.add.graphics();
    divider1.lineStyle(1, COLORS.borderSubtleInt, 0.6);
    divider1.lineBetween(
      left + 24,
      top + STATS_HEADER_HEIGHT,
      left + CARD_WIDTH - 24,
      top + STATS_HEADER_HEIGHT
    );
    container.add(divider1);

    // Aggregates strip — four big numbers across the top.
    const aggTop = top + STATS_HEADER_HEIGHT;
    this.drawAggregates(container, left, aggTop, stats);

    const divider2 = this.add.graphics();
    divider2.lineStyle(1, COLORS.borderSubtleInt, 0.6);
    divider2.lineBetween(
      left + 24,
      aggTop + STATS_AGG_HEIGHT,
      left + CARD_WIDTH - 24,
      aggTop + STATS_AGG_HEIGHT
    );
    container.add(divider2);

    // Per-player rows. Order: by finalTileCount descending (winner
    // first), to align with the podium card above.
    const orderedPlayers = [...players].sort(
      (a, b) => b[1].finalTileCount - a[1].finalTileCount
    );
    const myId = getAuth().user?.id ?? '';
    let rowTop = aggTop + STATS_AGG_HEIGHT;
    for (const [pid, p] of orderedPlayers) {
      this.drawStatsRow(container, left, rowTop, p, pid === myId);
      rowTop += STATS_ROW_HEIGHT;
    }

    return cardH;
  }

  /** Aggregates strip: TURNS · ATTACKS · TILES · MIN. Four equal
   *  columns across the card width. */
  private drawAggregates(
    container: Phaser.GameObjects.Container,
    cardLeft: number,
    top: number,
    stats: GameStats
  ): void {
    const cellW = CARD_WIDTH / 4;
    const values: { value: string; label: string }[] = [
      { value: String(stats.totalTurns), label: 'TURNS' },
      { value: String(stats.totalAttacks), label: 'ATTACKS' },
      { value: String(stats.totalConquests), label: 'CONQUESTS' },
      {
        value: stats.durationMinutes < 1
          ? '<1'
          : String(Math.round(stats.durationMinutes)),
        label: 'MINUTES',
      },
    ];
    for (let i = 0; i < values.length; i++) {
      const cx = cardLeft + i * cellW + cellW / 2;
      const v = values[i]!;
      const num = this.add
        .text(cx, top + 22, v.value, {
          fontFamily: FONT_HEADER,
          fontSize: '28px',
          color: COLORS.accentCyanHex,
          fontStyle: '700',
        })
        .setOrigin(0.5, 0);
      container.add(num);
      const label = this.add
        .text(cx, top + STATS_AGG_HEIGHT - 18, v.label, {
          fontFamily: FONT_MONO,
          fontSize: '10px',
          color: COLORS.textMuted,
        })
        .setLetterSpacing(2)
        .setOrigin(0.5, 0);
      container.add(label);
    }
  }

  /** One per-player row: seat-color chip + name + win-rate text on
   *  the left, dice fairness histogram on the right. */
  private drawStatsRow(
    container: Phaser.GameObjects.Container,
    cardLeft: number,
    top: number,
    p: PlayerStats,
    isLocal: boolean
  ): void {
    const innerLeft = cardLeft + 24;
    const midY = top + STATS_ROW_HEIGHT / 2;

    // Color chip
    const chipX = innerLeft + 14;
    const colorInt = Phaser.Display.Color.HexStringToColor(
      p.colorHex
    ).color;
    const chip = this.add
      .rectangle(chipX, midY - 14, 18, 18, colorInt)
      .setStrokeStyle(1, COLORS.borderBrightInt);
    container.add(chip);

    // Name
    const label = isLocal
      ? `${p.name.toUpperCase()}  (YOU)`
      : p.name.toUpperCase();
    const nameText = this.add
      .text(chipX + 16, midY - 14, label, {
        fontFamily: FONT_HEADER,
        fontSize: '15px',
        color: isLocal ? COLORS.accentCyanHex : COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(1.5)
      .setOrigin(0, 0.5);
    container.add(nameText);

    // Combat win-rate line below the name.
    const winLine = this.formatWinRate(p);
    const winText = this.add
      .text(chipX + 16, midY + 8, winLine, {
        fontFamily: FONT_MONO,
        fontSize: '10px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0.5);
    container.add(winText);

    // Dice fairness chart — right half of the row.
    const chartLeft = cardLeft + CARD_WIDTH / 2 + 8;
    const chartWidth = CARD_WIDTH / 2 - 32;
    const chartHeight = STATS_ROW_HEIGHT - 8;
    drawDiceFairnessChart({
      scene: this,
      parent: container,
      x: chartLeft,
      y: top + 4,
      width: chartWidth,
      height: chartHeight,
      diceRolls: p.diceRolls,
      totalDice: p.totalDice,
      colorHex: p.colorHex,
      showFaceLabels: true,
    });
  }

  /** "ATK 27/41 · DEF 8/14" or "no combats" if the player didn't
   *  participate in any dice rolls (eliminated very early). */
  private formatWinRate(p: PlayerStats): string {
    const atkTotal = p.attackerPairsWon + p.attackerPairsLost;
    const defTotal = p.defenderPairsWon + p.defenderPairsLost;
    if (atkTotal === 0 && defTotal === 0) return 'NO COMBATS';
    const parts: string[] = [];
    if (atkTotal > 0) {
      parts.push(`ATK ${p.attackerPairsWon}/${atkTotal}`);
    }
    if (defTotal > 0) {
      parts.push(`DEF ${p.defenderPairsWon}/${defTotal}`);
    }
    return parts.join('  ·  ');
  }
}

/**
 * Build the same podium GameOverModal builds, but from a raw state
 * (no Store available here). Survivors share rank 1; eliminated
 * players ordered last-out first take ranks 2..N.
 */
function buildPodium(state: GameState, myUserId: string): PodiumRow[] {
  if (state.status.kind !== 'victory') return [];
  const playerById = new Map(state.players.map((p) => [p.id, p]));
  const winner = playerById.get(state.status.winner);
  if (!winner) return [];

  // Territory count per player from current tile ownership.
  const tileCounts = new Map<PlayerId, number>();
  for (const t of Object.values(state.territories)) {
    if (t.owner == null) continue;
    tileCounts.set(t.owner, (tileCounts.get(t.owner) ?? 0) + 1);
  }

  const rows: PodiumRow[] = [];
  const survivors = state.players.filter((p) => !p.eliminated);
  const survivorsOrdered = [
    winner,
    ...survivors.filter((p) => p.id !== winner.id),
  ];
  for (const p of survivorsOrdered) {
    rows.push({
      rank: 1,
      player: p,
      isLocal: p.id === myUserId,
      tileCount: tileCounts.get(p.id) ?? 0,
    });
  }
  const reversed = [...state.eliminationOrder].reverse();
  let nextRank = 2;
  for (let i = 0; i < reversed.length && rows.length < 4; i++) {
    const id = reversed[i];
    if (!id) continue;
    const p = playerById.get(id);
    if (!p) continue;
    rows.push({
      rank: nextRank,
      player: p,
      isLocal: p.id === myUserId,
      tileCount: tileCounts.get(p.id) ?? 0,
    });
    nextRank++;
  }
  return rows;
}
